import React, { useMemo, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Checkbox,
    IconButton,
    Button,
    MenuItem
} from '@mui/material';
import { ReactTable } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AddConfig from './AddDatabaseConfig';
import { showToast } from '@utils';
import {
    useGetDbConfigsQuery,
    useDeleteDbConfigMutation
} from '../../../../../../store/feature/chat/knowledgeBase';

const Database = () => {
    const [isDatabaseEnabled, setIsDatabaseEnabled] = useState(false);
    const [selectedDatabaseIds, setSelectedDatabaseIds] = useState([]);
    const { data: dbConfigs, isFetching, refetch } = useGetDbConfigsQuery();
    const [showAddConfig, setShowAddConfig] = useState(false);

    // Handle navigation to AddConfig
    const handleAddDatabaseConnection = () => {
        setShowAddConfig(true);
    };

    // Handle navigation back to Database
    const handleBackToDatabase = () => {
        setShowAddConfig(false);
    };

    // Handle individual database selection
    const handleDatabaseSelect = useCallback(
        (databaseId) => {
            if (!isDatabaseEnabled) return;

            setSelectedDatabaseIds((prev) => {
                if (prev.includes(databaseId)) {
                    return prev.filter((id) => id !== databaseId);
                } else {
                    return [...prev, databaseId];
                }
            });
        },
        [isDatabaseEnabled]
    );

    // Handle select all databases
    const handleSelectAllDatabases = useCallback(() => {
        if (!isDatabaseEnabled) return;

        if (selectedDatabaseIds?.length === dbConfigs?.length) {
            setSelectedDatabaseIds([]);
        } else {
            setSelectedDatabaseIds(dbConfigs?.map((db) => db.id));
        }
    }, [isDatabaseEnabled, selectedDatabaseIds?.length, dbConfigs]);

    const [deleteDbConfig] = useDeleteDbConfigMutation();

    // Handle database deletion
    const handleDelete = useCallback((database) => {
        if (window.confirm('Are you sure you want to delete this database?')) {
            deleteDbConfig(database)
                .then(() => {
                    showToast('info', 'Database deleted successfully');
                    // Optionally, refresh the list of databases
                    refetch();
                })
                .catch((error) => {
                    showToast('error', error.message);
                });
        }
    }, [deleteDbConfig, refetch]);

    const columns = useMemo(
        () => [
            // {
            //     header: 'Select',
            //     id: 'select',
            //     size: 60,
            //     Cell: ({ row }) => (
            //         <Checkbox
            //             checked={selectedDatabaseIds.includes(row.original.id)}
            //             onChange={() => handleDatabaseSelect(row.original.id)}
            //             disabled={!isDatabaseEnabled}
            //             size="small"
            //         />
            //     )
            // },
            {
                header: 'Name',
                id: 'database',
                accessorFn: ({ database }) => database || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Type',
                id: 'database_type',
                accessorFn: ({ database_type }) => database_type || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Host',
                id: 'host',
                // host and port combined
                accessorFn: ({ host, port }) => `${host}:${port}` || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            },
            {
                header: 'Created',
                id: 'created_at',
                accessorFn: ({ created_at }) => created_at || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>
            }
        ],
        [selectedDatabaseIds, isDatabaseEnabled, handleDatabaseSelect]
    );

    const actionsMenu = useMemo(
        () =>
            ({ row }) =>
                [
                    <Button
                        variant="outlined"
                        color="error"
                        sx={{
                            p: 0,
                            m: 0,
                            borderColor: 'brown',
                            color: 'brown',
                            fontSize: '13px',
                            '&:hover': {
                                borderColor: 'brown',
                                color: 'white',
                                backgroundColor: 'brown'
                            }
                        }}
                        onClick={() => handleDelete(row.original.uuid)}
                    >
                        Delete
                    </Button>
                ],
        [handleDelete]
    );

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {showAddConfig ? (
                <AddConfig onBack={handleBackToDatabase} />
            ) : (
                /* Existing Connections Table */
                <ReactTable
                    columns={columns}
                    data={dbConfigs ?? []}
                    isLoading={isFetching}
                    actions={actionsMenu}
                    renderToolbarInternalActions={({ table }) => (
                        <Box onMouseDown={(e) => e.stopPropagation()}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                {/* Select All Checkbox */}
                                {isDatabaseEnabled && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <Checkbox
                                            checked={
                                                selectedDatabaseIds?.length ===
                                                    dbConfigs?.length &&
                                                dbConfigs?.length > 0
                                            }
                                            indeterminate={
                                                selectedDatabaseIds?.length >
                                                    0 &&
                                                selectedDatabaseIds?.length <
                                                    dbConfigs?.length
                                            }
                                            onChange={handleSelectAllDatabases}
                                            size="small"
                                        />
                                        <Typography variant="caption">
                                            Select All
                                        </Typography>
                                    </Box>
                                )}
                                {/* Selected Count */}
                                {isDatabaseEnabled &&
                                    selectedDatabaseIds?.length > 0 && (
                                        <Typography
                                            variant="caption"
                                            color="primary"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            ({selectedDatabaseIds?.length}{' '}
                                            selected)
                                        </Typography>
                                    )}

                                {/* Private Knowledge Switch */}
                                {/* <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isDatabaseEnabled}
                                            onChange={(e) => {
                                                setIsDatabaseEnabled(
                                                    e.target.checked
                                                );
                                                // Clear selections when disabled
                                                if (!e.target.checked) {
                                                    setSelectedDatabaseIds([]);
                                                }
                                            }}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label="Enable"
                                    labelPlacement="start"
                                    sx={{ mr: 2 }}
                                /> */}
                                <IconButton
                                    size="small"
                                    onClick={handleAddDatabaseConnection}
                                >
                                    <AddBoxOutlinedIcon />
                                </IconButton>
                                {/* eslint-disable-next-line react/jsx-pascal-case */}
                                <MRT_ToggleGlobalFilterButton table={table} />
                            </Box>
                        </Box>
                    )}
                    headerText={`Existing Connections (${dbConfigs?.length})`}
                />
            )}
        </Box>
    );
};

export default Database;
